/**
 * Facilitator Group Sessions Page - Academy Style
 * Displays group sessions with the vibrant Academy design system
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/auth/auth-context';
import { GroupSessionStatus, UserRole } from '@/types/enums';
import { useGroupSessionsWhereUserIsMember } from '@/features/sessions/hooks/use-group-sessions';
import { Navigate } from 'react-router-dom';
import { 
  Plus as PlusIcon, 
  Users as UserGroupIcon, 
  Calendar as CalendarIcon,
  MapPin as MapPinIcon,
  Sparkles as SparklesIcon,
  BarChart3 as ChartBarIcon,
  Clock as ClockIcon,
  CheckCircle as CheckCircleIcon,
  XCircle as XCircleIcon,
  GraduationCap as AcademicCapIcon,
  BookOpen as BookOpenIcon,
  User as UserIcon,
  LogOut as LogOutIcon,
  Menu as MenuIcon,
  X as XIcon,
  Home as HomeIcon
} from 'lucide-react';
import { formatDate } from '@/shared/utils/date-utils';
import { supabase } from '@/core/api/supabase';

const FacilitatorGroupSessionsPageAcademy: React.FC = () => {
  const { user, userRole, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<GroupSessionStatus | 'all'>('all');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  
  // Debug logging
  console.log('FacilitatorGroupSessions - Current user role:', userRole);
  console.log('FacilitatorGroupSessions - UserRole.FACILITATOR:', UserRole.FACILITATOR);
  console.log('FacilitatorGroupSessions - Role comparison:', userRole === UserRole.FACILITATOR);
  console.log('FacilitatorGroupSessions - Auth loading:', authLoading);
  
  // Check role before calling hooks that depend on having the right permissions
  // Handle both enum and string comparisons for flexibility
  const hasAccess = userRole === UserRole.FACILITATOR || 
                   userRole === UserRole.ADMIN ||
                   String(userRole).toLowerCase() === 'facilitator' ||
                   String(userRole).toLowerCase() === 'admin';
  
  console.log('FacilitatorGroupSessions - hasAccess:', hasAccess);
  console.log('FacilitatorGroupSessions - user?.id:', user?.id);
  
  // Call hook unconditionally but with a conditional enable flag
  const { data: sessions = [], isLoading, error } = useGroupSessionsWhereUserIsMember(
    statusFilter, 
    hasAccess ? user?.id : undefined
  );
  
  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 flex items-center justify-center">
        <div className="animate-pulse flex items-center space-x-3">
          <UserGroupIcon className="h-8 w-8 text-teal-600" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Redirect if not facilitator or admin
  if (!hasAccess) {
    console.log('FacilitatorGroupSessions - No access, redirecting to /unauthorized');
    return <Navigate to="/unauthorized" />;
  }

  // Status filter options
  const statusFilters = [
    { value: 'all', label: 'All Sessions' },
    { value: 'draft' as string, label: 'Draft' },
    { value: GroupSessionStatus.UPCOMING, label: 'Upcoming' },
    { value: 'pending' as string, label: 'Pending' },
    { value: 'in_progress' as string, label: 'In Progress' },
    { value: 'completed' as string, label: 'Completed' },
    { value: GroupSessionStatus.CANCELLED, label: 'Cancelled' },
  ];

  // Handle status filter change
  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value as GroupSessionStatus | 'all');
  };

  // Get status badge styling with gradients
  const getStatusBadge = (status: GroupSessionStatus | string) => {
    switch (status) {
      case 'draft':
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
      case GroupSessionStatus.UPCOMING:
        return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white';
      case 'pending':
        return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white';
      case 'in_progress':
        return 'bg-gradient-to-r from-green-500 to-green-600 text-white';
      case 'completed':
        return 'bg-gradient-to-r from-purple-500 to-purple-600 text-white';
      case GroupSessionStatus.CANCELLED:
        return 'bg-gradient-to-r from-red-500 to-red-600 text-white';
      default:
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
    }
  };

  // Get status icon
  const getStatusIcon = (status: GroupSessionStatus | string) => {
    switch (status) {
      case 'in_progress':
        return <ClockIcon className="h-4 w-4" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4" />;
      case GroupSessionStatus.CANCELLED:
        return <XCircleIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  // Calculate session stats
  const sessionStats = {
    total: sessions.length,
    upcoming: sessions.filter(s => s.status === GroupSessionStatus.UPCOMING).length,
    inProgress: sessions.filter(s => (s.status as string) === 'in_progress').length,
    completed: sessions.filter(s => (s.status as string) === 'completed').length,
  };

  // Handle logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/auth/login');
  };

  // Navigation items
  const navigationItems = [
    {
      name: "Facilitator's Course",
      icon: <BookOpenIcon className="h-5 w-5" />,
      path: '/facilitator/training/course/1',
      color: 'text-purple-400'
    },
    {
      name: 'Group Sessions',
      icon: <UserGroupIcon className="h-5 w-5" />,
      path: '/facilitator/group-sessions',
      color: 'text-teal-400',
      isActive: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
      {/* Top Navigation Bar */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Logo and Navigation */}
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                {showMobileMenu ? <XIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
              </button>
              
              {/* Logo */}
              <div className="flex items-center">
                <img src="/wwlogo.webp" alt="Wisdom Weaver Logo" className="h-10 w-auto mr-3" />
                <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-purple-600 bg-clip-text text-transparent">
                  Wisdom Weaver
                </span>
              </div>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center ml-10 space-x-4">
                {navigationItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                      item.isActive 
                        ? 'bg-gradient-to-r from-teal-500 to-purple-500 text-white shadow-md' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span className={item.isActive ? '' : item.color}>{item.icon}</span>
                    <span className="font-medium">{item.name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Right side - User Profile */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-gray-900">{user?.email?.split('@')[0] || 'User'}</p>
                  <p className="text-xs text-gray-500 capitalize">{userRole}</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-teal-400 to-purple-400 flex items-center justify-center text-white font-semibold">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
              </button>
              
              {/* Profile Dropdown */}
              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                  <button
                    onClick={() => navigate('/profile')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <UserIcon className="h-4 w-4" />
                    <span>Profile</span>
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <LogOutIcon className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setShowMobileMenu(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium ${
                    item.isActive 
                      ? 'bg-gradient-to-r from-teal-500 to-purple-500 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className={item.isActive ? '' : item.color}>{item.icon}</span>
                  <span>{item.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Header Section */}
      <div className="bg-gradient-to-r from-teal-600 to-purple-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <UserGroupIcon className="h-10 w-10 text-amber-400" />
              <div>
                <h1 className="text-3xl font-bold text-white">My Group Sessions</h1>
                <p className="text-teal-100 mt-1">Facilitate meaningful conversations and drive positive change</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/group-sessions/create-simple')}
              className="hidden md:inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-medium hover:shadow-lg transform hover:scale-105 transition-all"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create New Session
            </button>
          </div>

          {/* Stats Overview - Hidden on mobile */}
          <div className="mt-8 hidden md:grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-100 text-sm">Total Sessions</p>
                  <p className="text-2xl font-bold text-white">{sessionStats.total}</p>
                </div>
                <ChartBarIcon className="h-8 w-8 text-amber-400" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-100 text-sm">Upcoming</p>
                  <p className="text-2xl font-bold text-white">{sessionStats.upcoming}</p>
                </div>
                <CalendarIcon className="h-8 w-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-100 text-sm">In Progress</p>
                  <p className="text-2xl font-bold text-white">{sessionStats.inProgress}</p>
                </div>
                <ClockIcon className="h-8 w-8 text-green-400" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-100 text-sm">Completed</p>
                  <p className="text-2xl font-bold text-white">{sessionStats.completed}</p>
                </div>
                <CheckCircleIcon className="h-8 w-8 text-purple-400" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Section */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label htmlFor="status-filter" className="text-gray-700 font-medium">
              Filter by Status:
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            >
              {statusFilters.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <SparklesIcon className="h-4 w-4 text-amber-500" />
            <span>AI-enhanced facilitation tools available</span>
          </div>
        </div>

        {/* Sessions Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-pulse flex items-center space-x-3">
              <UserGroupIcon className="h-8 w-8 text-teal-600" />
              <p className="text-gray-600">Loading your sessions...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <XCircleIcon className="h-8 w-8 text-red-600" />
            </div>
            <p className="text-red-600 mb-2">Error loading sessions</p>
            <p className="text-gray-600 text-sm">{error.toString()}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:shadow-lg transition-all"
            >
              Try Again
            </button>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-100 to-purple-100 rounded-full mb-6">
              <UserGroupIcon className="h-10 w-10 text-teal-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Sessions Yet</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              {statusFilter !== 'all' 
                ? `No sessions with status "${statusFilters.find(f => f.value === statusFilter)?.label}" found.`
                : "Start your facilitation journey by creating your first group session."}
            </p>
            <button
              onClick={() => navigate('/group-sessions/create-simple')}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-medium hover:shadow-lg transform hover:scale-105 transition-all"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Your First Session
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sessions.map((session) => (
              <Link
                key={session.id}
                to={`/group-sessions/${session.id}`}
                className="group block"
              >
                <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
                  {/* Session Image */}
                  {session.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={session.image_url} 
                        alt={session.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  
                  {/* Card Header with Gradient */}
                  <div className="h-2 bg-gradient-to-r from-teal-500 to-purple-500"></div>
                  
                  <div className="p-6">
                    {/* Status Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(session.status)}`}>
                        {getStatusIcon(session.status)}
                        <span>{session.status.replace('_', ' ')}</span>
                      </span>
                      {session.userRole && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {session.userRole}
                        </span>
                      )}
                    </div>

                    {/* Session Title */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {session.title}
                    </h3>

                    {/* Session Details */}
                    <div className="space-y-2 text-sm text-gray-600">
                      {session.start_date && (
                        <div className="flex items-center space-x-2">
                          <CalendarIcon className="h-4 w-4 text-gray-400" />
                          <span>{formatDate(session.start_date, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}</span>
                        </div>
                      )}
                      {session.location && (
                        <div className="flex items-center space-x-2">
                          <MapPinIcon className="h-4 w-4 text-gray-400" />
                          <span>{session.location}</span>
                        </div>
                      )}
                      {session.facilitator_name && (
                        <div className="flex items-center space-x-2">
                          <UserGroupIcon className="h-4 w-4 text-gray-400" />
                          <span>Facilitator: {session.facilitator_name}</span>
                        </div>
                      )}
                    </div>

                    {/* Hover Effect */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <span className="text-sm text-teal-600 group-hover:text-teal-700 font-medium">
                        View Details →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Floating Action Button - Mobile Only */}
      <button
        onClick={() => navigate('/group-sessions/create-simple')}
        className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all flex items-center justify-center z-50"
        aria-label="Create New Session"
      >
        <PlusIcon className="h-6 w-6" />
      </button>
    </div>
  );
};

export default FacilitatorGroupSessionsPageAcademy;